import { useEffect, useMemo, useRef, useState } from "react";
import { getTimeZones, TimeZone } from "@vvo/tzdb";
import { TimezoneOption } from "../timezones.types";
import { getTimezoneOptions } from "../utils/getTimezoneOptions";
import { makeTimezoneOptionFromTzdbTimezone } from "../utils/makeTimezoneOptionFromTzdbTimezone";

export type Args = {
  /** User's timezone string (e.g., "America/New_York" or "UTC") */
  userTimezone?: string | null;
};

/**
 * Get the user's timezone option from their stored timezone string.
 */
function getUserTimezoneOption(
  userTimezone: string | null | undefined
): TimezoneOption | null {
  if (!userTimezone) return null;

  const timezones = getTimeZones({ includeUtc: true });

  const foundTimezone = timezones.find((z) => {
    const isUtcGroup = z.name === "Etc/UTC" || z.group.includes("Etc/UTC");
    const value = isUtcGroup ? "UTC" : z.name;
    return value === userTimezone;
  });

  if (!foundTimezone) {
    return null;
  }

  return makeTimezoneOptionFromTzdbTimezone(foundTimezone as TimeZone);
}

/**
 * Hook to manage timezone selection state.
 *
 * @param args.userTimezone - The user's stored timezone string (from user profile)
 * @returns Timezone options, selected timezone, and change handler
 */
export function useTimezoneOptions({ userTimezone }: Args = {}) {
  const timezones = getTimezoneOptions();

  // Memoize user timezone option to avoid recalculating on every render
  const userTimezoneOption = useMemo(
    () => getUserTimezoneOption(userTimezone),
    [userTimezone]
  );

  const [selectedTimezone, setSelectedTimezone] =
    useState<TimezoneOption | null>(null);

  // Track if we've initialized from user timezone
  const hasInitialized = useRef(false);

  // Only set initial timezone once when userTimezoneOption becomes available
  useEffect(() => {
    if (hasInitialized.current) return;
    if (!timezones) return;
    if (!userTimezoneOption) return;

    setSelectedTimezone(userTimezoneOption);
    hasInitialized.current = true;
  }, [timezones, userTimezoneOption]);

  const handleTimezoneChange = (option: TimezoneOption | null) => {
    setSelectedTimezone(option as TimezoneOption);
  };

  return {
    timezones,
    selectedTimezone,
    handleTimezoneChange,
  };
}

export default useTimezoneOptions;

